import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Table,
  Title,
} from "@mantine/core";
import { v4 as uuidv4 } from "uuid";

import {
  ADMIN_ORGANIZATION,
  CLICK_AND_COLLECT_CREATE_AUTHORISED_ORGANIZATIONS,
  CLICK_AND_COLLECT_CREATE_AUTHORISED_ROLES,
  CLICK_AND_COLLECT_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  CLICK_AND_COLLECT_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
} from "_roles";
import { AccessRights } from "common/admin/auth/AccessRights";
import { ClickAndCollectBreadcrumbs } from "../components/ClickAndCollectBreadcrumbs";
import { useListFoodMenusQuery } from "click-and-collect/api/foodMenu.api";
import { useOrganization } from "common/admin/auth/hooks";

import type {
  FoodMenu,
  FoodMenuFormMode,
} from "click-and-collect/types/foodMenu.model";
import { FoodMenuForm } from "../components/FoodMenuForm";

export function ClickAndCollectAdminListView() {
  const [organization] = useOrganization();

  const [foodMenuFormMode, setFoodMenuFormMode] =
    useState<FoodMenuFormMode>("read");

  const setReadOnly = () => setFoodMenuFormMode("read");

  const {
    isLoading,
    isFetching,

    isError,
    error,

    refetch,
    data: foodMenus,
  } = useListFoodMenusQuery();

  const filteredFoodMenus = useMemo(
    () =>
      (foodMenus || []).filter(
        (foodMenu) => foodMenu.creationTime && foodMenu.label,
      ),
    [foodMenus],
  );

  const ths = (
    <tr>
      <th>Title</th>
      <AccessRights
        authorizedOrganizations={
          CLICK_AND_COLLECT_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
        }
        authorizedRoles={
          CLICK_AND_COLLECT_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES
        }
      >
        <th>Organization</th>
      </AccessRights>

      <th>Date</th>
      <th>Document</th>
      <th>Actions</th>
    </tr>
  );

  const rows = useCallback(() => {
    let filledRows: JSX.Element[] = [];
    if (organization) {
      filledRows =
        filteredFoodMenus && filteredFoodMenus.length > 0
          ? filteredFoodMenus
              .filter(
                (menu: FoodMenu) =>
                  organization === ADMIN_ORGANIZATION ||
                  menu.organization === organization,
              )
              .map((foodMenu: FoodMenu) => {
                return (
                  <FoodMenuForm
                    key={uuidv4()}
                    foodMenu={foodMenu}
                    initialMode="read"
                    organization={organization}
                    setReadOnly={setReadOnly}
                    refetch={refetch}
                  />
                );
              })
          : [];

      if (foodMenuFormMode === "creation") {
        const newFoodMenuForm = (
          <FoodMenuForm
            key={uuidv4()}
            initialMode="creation"
            organization={organization}
            setReadOnly={setReadOnly}
            refetch={refetch}
          />
        );
        filledRows.push(newFoodMenuForm);
      }
    }
    return filledRows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredFoodMenus, foodMenuFormMode, organization]);

  useEffect(() => {
    if (isError) {
      console.error("Get FoodMenu list error", JSON.stringify(error));
    }
  }, [isError, error]);

  return (
    <Box>
      <div
        className="sticky top-0 z-50 pb-4"
        style={{ backgroundColor: "#E9ECEF" }}
      >
        <Title order={3} align={"center"} m={"xs"}>
          List of Menus
        </Title>
        <ClickAndCollectBreadcrumbs />
        <AccessRights
          authorizedRoles={CLICK_AND_COLLECT_CREATE_AUTHORISED_ROLES}
          authorizedOrganizations={
            CLICK_AND_COLLECT_CREATE_AUTHORISED_ORGANIZATIONS
          }
        >
          <Group position={"right"}>
            <Button
              className="bg-button-admin"
              onClick={() => setFoodMenuFormMode("creation")}
            >
              New
            </Button>
          </Group>
        </AccessRights>
      </div>
      {isFetching || isLoading ? (
        <Card shadow={"sm"} p={"xl"} className={"overflow-visible"}>
          <Stack align={"center"}>
            <Loader />
          </Stack>
        </Card>
      ) : null}
      {!isFetching ? (
        <Card shadow={"sm"} p={"xl"} className={"overflow-visible"}>
          <Table highlightOnHover>
            <thead>{ths}</thead>
            <tbody>{rows()}</tbody>
            <tfoot></tfoot>
          </Table>
        </Card>
      ) : null}
    </Box>
  );
}
